import { easings } from "@apps/viewer/src/ts/config";
import React from "react";

type ChevronProps = {
    /**
     * Specifies if the chevron should inverse (e.g. to be used when selected)
     */
    inversed?: boolean;

    /**
     * Specifies if the chevron should be vertical (i.e. move up and down)
     */
    vertical?: boolean;

    /**
     * Specifies the length of the lines in the chevron, given in pixels.
     */
    size?: number;

    /**
     * Specifies the toggle transition duration, when the chevron is being
     * inversed.
     */
    inverseDuration?: number;
};

/**
 * This UI component is responsible for rendering a reusable chevron
 */
export class Chevron extends React.PureComponent<ChevronProps> {
    /**
     * Main render function
     */
    public render(): React.JSX.Element {
        const { vertical, inversed } = this.props;
        const size = this.props.size || 10;
        const inverseDuration = this.props.inverseDuration || 175;

        return (
            <i className={`${ vertical ? "vertical" : "" } ${ inversed ? "inversed" : "" }`}>
                <style jsx>{`
                    i {
                        /**
                         * Automatically center the chevron inside it's
                         * container.
                         */
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        margin-top: -${ size * 0.7 }px;
                        margin-left: -${ size * 0.5 }px;

                        /* Setup fixed dimensions */
                        width: ${ size }px;
                        height: ${ size * 1.4 }px;

                        /**
                         * Allow the pseudo child elements to inherit
                         * border-color from the parent elements
                         */
                        border-color: inherit;

                        /**
                         * Prepare inverse transition
                         * Fixes IE10/11 renderbug by offloading to GPU as well
                         */
                        transform: translate3d(${ size * 0.1 }px, 0, 0);
                        transition: transform ${ inverseDuration }ms ${ easings.inOutCubic };

                        /**
                         * Use the pseudo :before and :after elements to render
                         * the lines in the chevron.
                         */
                        & ::before,
                        & ::after {
                            /** Force the elements to be rendered */
                            content: '';

                            /** Each line should be absolutely positioned */
                            position: absolute;
                            top: ${ size * 0.6 }px;
                            right: ${ size * 0.1 }px;

                            /** Setup dimensions */
                            width: ${ size }px;
                            height: 0;

                            /** Render the line using border-top */
                            border-top-width: ${ size * 0.2 }px;
                            border-top-style: solid;
                            border-top-color: inherit;

                            /**
                             * Prepare transitions between up / down and left /
                             * right
                             */
                            transition:
                                transform ${ inverseDuration }ms ${ easings.inOutCubic },
                                right ${ inverseDuration }ms ${ easings.inOutCubic },
                                border-color 0.1s linear;
                        }

                        /* Rotate one line clockwise */
                        & ::before {
                            transform: rotate(45deg);

                            /**
                             * Rotate from the right-most center point that will
                             * be shared between the two lines (ie. with an even
                             * 0.1 * size margin to either side).
                             */
                            transform-origin: ${ size * 0.9 }px ${ size * 0.1 }px;
                        }

                        /* ... And the other counter-clockwise */
                        & ::after {
                            transform: rotate(-45deg);

                            /**
                             * Rotate from the right-most center point that will
                             * be shared between the two lines (ie. with an even
                             * 0.1 * size margin to either side).
                             */
                            transform-origin: ${ size * 0.9 }px ${ size * 0.1 }px;
                        }
                    }

                    .vertical {
                        transform: rotate(90deg) translate3d(0.05em, 0, 0);
                    }

                    .inversed {
                        transform: translate3d(-${ size * 0.65 }px, 0, 0);

                        & ::before {
                            transform: rotate(135deg);
                        }

                        & ::after {
                            transform: rotate(-135deg);
                        }
                    }

                    .inversed.vertical {
                        transform: rotate(90deg) translate3d(-${ size * 0.65 }px, 0, 0);
                    }
                `}</style>
            </i>
        );
    }
}
